import { useState, useEffect } from "react";
import axios from "axios";
import { PartyPopper, X, Loader2, Mail, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CelebrateModal({ open, onClose, prefill = {} }) {
  const [squads, setSquads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [company, setCompany] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCompany(prefill.company || "");
    setJobTitle(prefill.job_title || "");
    setMessage(""); setSelected([]);
    setLoading(true);
    axios.get(`${API}/squads/mine`)
      .then((r) => { const s = r.data.squads || []; setSquads(s); setSelected(s.map((x) => x.id)); })
      .catch(() => setSquads([]))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least one squad"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/squads/celebrate`, {
        squad_ids: selected, company, job_title: jobTitle, message,
        send_email: sendEmail, application_id: prefill.application_id,
      });
      toast.success(`Shared with ${res.data.celebrated_squads.length} squad${res.data.celebrated_squads.length !== 1 ? "s" : ""}! 🎉`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to share"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" data-testid="celebrate-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(480px, 100%)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><PartyPopper size={22} style={{ color: "#10b981" }} /> You got a job?!</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: 0 }}>Congrats! 🎉 Share the win with your squad(s) and inspire the crew.</p>

        <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Job title</label>
        <input className="modal-input" data-testid="celebrate-title-input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Senior Backend Engineer" style={{ width: "100%", marginBottom: 10 }} />
        <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Company (optional)</label>
        <input className="modal-input" data-testid="celebrate-company-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Inc" style={{ width: "100%", marginBottom: 10 }} />
        <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>A note for your squad (optional)</label>
        <textarea className="modal-input" data-testid="celebrate-message-input" value={message} rows={2} maxLength={300} onChange={(e) => setMessage(e.target.value)} placeholder="Thanks for keeping me going!" style={{ width: "100%", marginBottom: 14, resize: "vertical" }} />

        <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>Share with</label>
        {loading ? <div style={{ color: "var(--text-secondary)" }}><Loader2 size={15} className="spin" /> Loading squads…</div>
          : squads.length === 0 ? <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: 12 }}>You're not in any squads yet.</div>
          : <div style={{ marginBottom: 14 }}>
              {squads.map((s) => (
                <label key={s.id} data-testid={`celebrate-squad-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "0.5rem 0", cursor: "pointer" }}>
                  <span onClick={() => toggle(s.id)} style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${selected.includes(s.id) ? "#10b981" : "var(--border-main)"}`, background: selected.includes(s.id) ? "#10b981" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {selected.includes(s.id) && <Check size={13} color="#fff" />}
                  </span>
                  <span onClick={() => toggle(s.id)}>{s.name} <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>· {s.member_count} members</span></span>
                </label>
              ))}
            </div>}

        <label data-testid="celebrate-email-toggle" onClick={() => setSendEmail((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "0.6rem 0.8rem", borderRadius: 8, background: "var(--bg-surface-hl)", marginBottom: 16 }}>
          <span style={{ width: 20, height: 20, borderRadius: 6, border: `1.5px solid ${sendEmail ? "#6366f1" : "var(--border-main)"}`, background: sendEmail ? "#6366f1" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {sendEmail && <Check size={13} color="#fff" />}
          </span>
          <Mail size={15} /> <span style={{ fontSize: "0.86rem" }}>Also email my squad-mates</span>
        </label>

        <button className="btn-primary" data-testid="celebrate-submit-btn" onClick={submit} disabled={submitting || squads.length === 0} style={{ width: "100%", background: "#10b981" }}>
          {submitting ? <Loader2 size={16} className="spin" /> : <PartyPopper size={16} />} Celebrate with my Squad
        </button>
      </div>
    </div>
  );
}
